import { defineStore } from "pinia";

/**
 * Held outside the store: it is a browser handle, not state anyone renders, and keeping it
 * in `state` would make it part of every subscriber's reactive payload.
 */
let controlsFlashTimer: ReturnType<typeof setTimeout> | null = null;

/** Long enough to read the jump, short enough not to sit over the picture. */
const CONTROLS_FLASH_MS = 1500;
const DEFAULT_PLAYBACK_RATE = 2;
type PlaybackRate = 1 | 2 | 3;

/**
 * How far the picture is turned to stand upright, clockwise. The worker normalises every
 * recording into one canvas and rotates the picture into it when the device turns, so part of
 * a run can play on its side and nothing in the file says when (N7AIT-1122 moved the problem
 * rather than removing it). Driven by hand from the control bar until the worker reports the
 * turns; then the same value comes from the timeline and the button stays as an override.
 */
export type RotationDegrees = 0 | 90 | 180 | 270;

/**
 * Where the button puts the picture: turned left, the way the device itself turns.
 *
 * The type keeps all four quarters because the worker's timings will one day set this and
 * a device can turn either way — but a reader straightening a recording by hand wants one
 * click there and one click back, not a lap of 360°.
 */
const TURNED_LEFT: RotationDegrees = 270;

export const useVideoPlayerStore = defineStore("videoPlayer", {
  state: () => ({
    currentTime: 0,
    duration: 0,
    playing: false,
    sidePlayerOpen: false,
    videoRef: null as HTMLVideoElement | null,
    scrubbing: false,
    playbackRate: DEFAULT_PLAYBACK_RATE as PlaybackRate,
    pendingSeekMs: null as number | null,
    isFullscreen: false,
    controlsFlashing: false,
    rotationDeg: 0 as RotationDegrees,
  }),

  getters: {
    currentTimeMs: (state) => Math.floor(state.currentTime * 1000),
    durationMs: (state) => Math.floor(state.duration * 1000),
    isVideoFinished: (state) => state.currentTime >= state.duration,
    isPlaying: (state) => state.playing && !state.videoRef?.paused,
    /** Whether width and height are swapped on screen — the only distinction layout cares about. */
    isQuarterTurned: (state) => state.rotationDeg % 180 !== 0,
  },

  actions: {
    enterFullscreen() {
      this.isFullscreen = true;
    },

    exitFullscreen() {
      this.isFullscreen = false;
    },

    setCurrentTime(time: number) {
      this.currentTime = time;
    },

    setDuration(value: number) {
      this.duration = value;
    },

    setSidePlayerOpen(value: boolean) {
      this.sidePlayerOpen = value;
    },

    pause() {
      if (this.videoRef) this.videoRef.pause();
      this.playing = false;
    },

    async play() {
      if (!this.videoRef) {
        this.playing = false;
        return;
      }
      try {
        await this.videoRef.play();
        this.playing = true;
      } catch {
        this.playing = false;
      }
    },

    openPlayer() {
      this.resetSpeed();
      this.setSidePlayerOpen(true);
      this.seekTo(0);
      this.play();
    },

    setPlaybackRate(playbackRate: PlaybackRate) {
      this.playbackRate = playbackRate;
      if (this.videoRef) this.videoRef.playbackRate = playbackRate;
    },

    resetSpeed() {
      this.setPlaybackRate(DEFAULT_PLAYBACK_RATE);
    },

    toggleRotation() {
      this.rotationDeg = this.rotationDeg === 0 ? TURNED_LEFT : 0;
    },

    cyclePlaybackRate() {
      const nextRate: PlaybackRate = this.playbackRate === 1 ? 2 : this.playbackRate === 2 ? 3 : 1;
      this.setPlaybackRate(nextRate);
    },

    closePlayer() {
      this.resetSpeed();
      this.seekTo(0);
      this.pause();
      this.setSidePlayerOpen(false);
    },

    beginScrubbing() {
      this.scrubbing = true;
      this.pause();
    },

    endScrubbing(progress: number) {
      if (progress < 0 || progress > 1) return;
      this.scrubbing = false;
      this.seekTo(progress * this.durationMs);
      this.play();
    },

    togglePlay() {
      if (this.isPlaying) this.pause();
      else this.play();
    },

    seekTo(timeMs: number) {
      if (timeMs < 0) return;

      // The recording's length is unknown until `loadedmetadata` fires, and so is the
      // element while the player is still mounting. A seek arriving before either — a
      // step clicked the moment the page opens — is held rather than dropped; the player
      // replays it from `handleLoadedMetadata`.
      if (!this.videoRef || this.durationMs === 0) {
        this.pendingSeekMs = timeMs;
        return;
      }

      if (timeMs > this.durationMs) return;

      this.currentTime = timeMs / 1000;
      this.videoRef.currentTime = this.currentTime;
    },

    /** Move relative to the current position, stopping at either end of the recording. */
    seekBy(offsetMs: number) {
      if (!this.videoRef || !Number.isFinite(this.videoRef.duration) || this.videoRef.duration <= 0) return false;
      if (offsetMs > 0 && (this.videoRef.ended || this.videoRef.currentTime >= this.videoRef.duration)) return false;

      const targetTime = Math.min(this.videoRef.duration, Math.max(0, this.videoRef.currentTime + offsetMs / 1000));
      if (targetTime === this.videoRef.currentTime) return false;
      this.videoRef.currentTime = targetTime;
      this.setCurrentTime(targetTime);
      return true;
    },

    /**
     * Show the control bar briefly, then let it hide again.
     *
     * A seek driven from outside the player — using a step’s video button — moves the
     * playhead while the bar is hidden, so nothing on screen says the recording jumped.
     * Flashing the bar shows the new position on the timeline.
     */
    flashControls() {
      this.controlsFlashing = true;
      if (controlsFlashTimer) clearTimeout(controlsFlashTimer);
      controlsFlashTimer = setTimeout(() => {
        this.controlsFlashing = false;
        controlsFlashTimer = null;
      }, CONTROLS_FLASH_MS);
    },

    setVideoRef(videoElement: HTMLVideoElement | null) {
      this.videoRef = videoElement;
      if (videoElement) videoElement.playbackRate = this.playbackRate;
    },

    /**
     * Must stay synchronous. On back/forward the cycle comes from the query cache and the next
     * player mounts and reports its recording within a few ms of the navigation guard calling
     * this — a deferred second pass would wipe that player's element and duration.
     */
    reset() {
      this.currentTime = 0;
      this.duration = 0;
      this.playing = false;
      this.sidePlayerOpen = false;
      this.videoRef = null;
      this.resetSpeed();
      this.scrubbing = false;
      // A turn belongs to the recording it was made for; the next one starts upright.
      this.rotationDeg = 0;
      // A seek held for a recording we are leaving must not fire into the next one.
      this.pendingSeekMs = null;
      if (controlsFlashTimer) clearTimeout(controlsFlashTimer);
      controlsFlashTimer = null;
      this.controlsFlashing = false;
    },

    /** Hands the player the seek that was held while the recording loaded, once. */
    consumePendingSeek() {
      const timeMs = this.pendingSeekMs;
      this.pendingSeekMs = null;
      return timeMs;
    },
  },
});
